import { forwardRef } from 'react'

import type { LinkProps } from 'next/link'
import Link from 'next/link'
import { useRouter } from 'next/router'
import type { PropsWithChildren } from 'react'

import { useCountryFromRegionProvider } from '@cash-web/shared-region-ui-context-main'
import { defaultRegion } from '@cash-web/support/utils-region-config'

import { fallbackLng as defaultLocale } from '../../../next-i18next.config'

interface RegionalisedLinkProps
  extends Omit<LinkProps, 'href'>, Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof LinkProps> {
  href: LinkProps['href']
  onClick?: (event: { preventDefault: () => void }) => void
}

const getRegionalisedHref = (href: LinkProps['href'], region: string): LinkProps['href'] => {
  if (typeof href === 'string') {
    const isInternalPath = href.startsWith('/')
    return isInternalPath ? `/${region}${href}` : href
  }

  // UrlObject
  const pathname = href.pathname ?? ''
  const isInternalPath = pathname.startsWith('/')
  if (!isInternalPath) return href

  return {
    ...href,
    pathname: `/${region}${pathname}`,
  }
}

const getRegionalisedHrefWithLocale = (href: LinkProps['href'], region: string, locale: string): LinkProps['href'] => {
  if (typeof href === 'string') {
    return href.startsWith('/') ? `/${region}/${locale}${href}` : href
  }

  const pathname = href.pathname ?? ''
  return pathname.startsWith('/') ? { ...href, pathname: `/${region}/${locale}${pathname}` } : href
}

const RegionalisedLink = forwardRef<HTMLAnchorElement, PropsWithChildren<RegionalisedLinkProps>>(
  ({ children, ...props }, ref) => {
    const { locale } = useRouter()
    const region = useCountryFromRegionProvider()?.toLowerCase() ?? defaultRegion

    const routingHref = getRegionalisedHref(props.href, region)
    const currentLocale = locale && locale !== 'default' ? locale : defaultLocale

    // Show /{region}/{locale}/{path} in the browser URL via `as` on staging/production.
    // Skipped in dev because Next.js adds its own locale prefix, which would double up.
    const displayHref =
      process.env.NODE_ENV !== 'development'
        ? getRegionalisedHrefWithLocale(props.href, region, currentLocale)
        : undefined

    return (
      <Link {...props} href={routingHref} as={displayHref} ref={ref}>
        {children}
      </Link>
    )
  }
)

RegionalisedLink.displayName = 'RegionalisedLink'

export default RegionalisedLink
